import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { CprPricingService } from '@usf/ngrx-pricing';
import { SellerPriceDetails } from '@usf/price-types';
import { Subscription } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { HandPricingDetails } from '../../../order/models/order-view-model';
import { CprPricingState } from '@usf/ngrx-pricing/lib/models/cpr-pricing.model';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { ToastService } from '@shared/services/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { CustomPricingRationaleManualModalService } from '@product-detail/services/custom-pricing-rationale-manual-modal.service';
import {
  allowOnlyNumbers,
  getPricesForPriceRadios,
  getRequestType,
  roundPrice,
} from '@product-detail/utils/cpr.util';
import { take } from 'rxjs/operators';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { OrderTracingService } from '@order/tracking/tracing/order-tracing.service';
import { OrderHeader } from '@usf/ngrx-order';
import { TracingProduct } from '@shared/models/tracking/tracing/tracing-product';
import { ShippingChargeEnum } from '@product-detail/models/shipping-charge.enum';
import { CustomerStoreService } from '@app/ngrx-customer/services';
import { FEATURES } from '@shared/constants/splitio-features';
import { formatLast4BuysList } from '@product-detail/utils/product-last-buys.util';
import { LastBuy } from '@product-detail/models/last-buy.model';

type RadioGroup = {
  label: string;
  price: number;
  id: string;
};

const RadioOptions = {
  minimum: 'minimum',
  maximum: 'maximum',
  target: 'target',
  custom: 'custom',
  makeCpr: 'makeCpr',
};

@Component({
  selector: 'app-hand-pricing-cpr-ngp-modal',
  templateUrl: './hand-pricing-cpr-ngp-modal.component.html',
  styleUrls: ['./hand-pricing-cpr-ngp-modal.component.scss'],
})
export class HandPricingCPRandNGPComponent implements OnInit, OnDestroy {
  @Input() sellerPriceDetail: SellerPriceDetails;
  @Input() altSellerPriceDetail: SellerPriceDetails;
  @Input() currentPriceOverride: number;
  @Input() sellerHandPricingDetail: HandPricingDetails;
  @Input() altHandPricingDetail: HandPricingDetails;
  @Input() districtNgpCprInd: string;
  @Input() isCprSetupInd: string;
  @Input() priceUom: string;
  @Input() orderHeader: OrderHeader;
  @Input() productTracing: TracingProduct[];

  lowLimitFeatureFlag = FEATURES.split_global_ngp_cpr_low_limit.name;
  pmeLowLimitFeatueFlag = FEATURES.split_dynamic_pme_low_limit.name;
  pmeSgpFeatureFlag = FEATURES.split_dynamic_pme_sgp.name;

  priceChange: number;
  thumbnailImageURL: string;
  tandemSystem: string;
  currentDivisionSub$: Subscription = new Subscription();
  cprPricingServiceSub$: Subscription = new Subscription();
  // RADIO GROUP AND INPUT
  private readonly EMPTY_VALUE: string = '';
  isInputDisabled = true;
  priceRadios: Map<string, RadioGroup>;
  priceRadioSelected: string;
  priceInputSelected: string;
  sellerFloorLabel: string;
  sellerFloorValue: number;
  userHasSelectedPrice = false;
  isMakeCprSelected = false;
  targetDifference: number;
  targetDifferencePercentage = 0;

  prices: HandPricingDetails = undefined;
  isSubstitute = false;
  includeAscCharge = false;
  customPrice = new UntypedFormControl(0);
  currentTab = 1;
  selectedPriceType = new UntypedFormControl('custom');
  additionalPricingAccordionLabel =
    'i18n.orderPage.cprNgpModal.additionalPricing';
  tabSwitched = false;
  isLowLimitEnabled = false;

  lowLimitFeatureFlagSub$: Subscription = new Subscription();
  recentPurchaseFeatureFlagSub$: Subscription;
  lastBuy: LastBuy;
  constructor(
    private modalController: ModalController,
    public readonly platformService: PlatformService,
    private readonly cprPricingService: CprPricingService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private loadingSpinnerService: LoadingSpinnerService,
    private customPricingRationaleManualModalService: CustomPricingRationaleManualModalService,
    private orderTracingService: OrderTracingService,
    private customerStoreService: CustomerStoreService,
    private panAppState: PanAppState,
  ) {}

  ngOnInit() {
    this.priceRadioSelected = this.EMPTY_VALUE;
    this.thumbnailImageURL = this.sellerHandPricingDetail?.thumbnailImageURL;
    if (this.sellerPriceDetail) {
      this.setPriceChange();
    }
    this.isSubstitute = !this.sellerHandPricingDetail?.description;
    this.includeAscCharge =
      this.sellerPriceDetail?.customerAscInd == ShippingChargeEnum.included;
    this.prices = !!this.sellerHandPricingDetail?.description
      ? this.sellerHandPricingDetail
      : this.altHandPricingDetail;
    this.priceRadios = getPricesForPriceRadios(this.sellerPriceDetail);
    this.setCustomPriceTargetDifference();
    this.lowLimitFeatureFlagSub$ = this.panAppState
      .feature$([this.lowLimitFeatureFlag])
      .subscribe(isLowLimitEnabled => {
        this.isLowLimitEnabled = isLowLimitEnabled;
        this.getSellerFloorValues();
      });
    this.recentPurchaseFeatureFlagSub$ = this.panAppState
      .feature$([FEATURES.split_global_cpr_last_puchase.name])
      .subscribe(isRecentPurchaseEnabled => {
        if (isRecentPurchaseEnabled && this.districtNgpCprInd === 'Y') {
          this.lastBuy = formatLast4BuysList(
            this.sellerPriceDetail?.lastFourBuys?.lastbuyList,
            this.prices,
          )[0];
        }
      });
    this.loadDivision();
  }

  ngOnDestroy(): void {
    this.currentDivisionSub$.unsubscribe();
    this.recentPurchaseFeatureFlagSub$?.unsubscribe();
    if (this.lowLimitFeatureFlagSub$) {
      this.lowLimitFeatureFlagSub$.unsubscribe();
    }
  }

  public get RadioOptions() {
    return RadioOptions;
  }

  getSellerFloorValues() {
    this.sellerFloorLabel = this.isLowLimitEnabled
      ? this.translateService.instant('i18n.orderPage.cprNgpModal.lowLimit')
      : this.translateService.instant('i18n.orderPage.cprNgpModal.sellerFloor');

    const commissionAmount = this.isLowLimitEnabled
      ? this.sellerPriceDetail?.zilliantDetails?.cprLowLimit
      : this.prices?.additionalPricingInfo?.commissionAmount;
    this.sellerFloorValue = this.includeAscCharge
      ? commissionAmount + this.prices?.additionalPricingInfo?.unitAscRate
      : commissionAmount;
  }

  getPriceToUpdate(): number {
    const priceToUpdate = this.priceRadios.get(this.priceRadioSelected)?.price
      ? this.priceRadios.get(this.priceRadioSelected)?.price
      : +this.priceInputSelected;
    return priceToUpdate;
  }

  updatePrice() {
    const priceToUpdate = this.getPriceToUpdate();
    if (priceToUpdate) {
      let priceOverrideWithAsc =
        this.sellerPriceDetail?.customerAscInd ===
          ShippingChargeEnum.included && this.priceRadioSelected !== 'custom'
          ? priceToUpdate + this.prices?.additionalPricingInfo?.unitAscRate || 0
          : priceToUpdate;
      this.unitPriceOverride(priceOverrideWithAsc);

      if (this.isMakeCprSelected) {
        this.sendUpdateCprPriceRequest(priceToUpdate);
      }
    }
    if (!this.userHasSelectedPrice) {
      return;
    }
  }

  sendUpdateCprPriceRequest(priceToUpdate: number): void {
    this.traceCpr(priceToUpdate);
    const ascCharge = this.prices?.additionalPricingInfo?.unitAscRate || 0;
    const priceDetail = !this.isSubstitute
      ? this.sellerPriceDetail
      : this.altSellerPriceDetail;
    let newPrice = priceToUpdate;
    if (
      this.sellerPriceDetail?.customerAscInd === ShippingChargeEnum.included
    ) {
      newPrice = priceToUpdate - ascCharge > 0 ? priceToUpdate - ascCharge : 0;
    }
    const roundedPrice = roundPrice(newPrice);
    this.loadingSpinnerService.createSpinnerModal().then(() => {
      this.cprPricingServiceSub$ = this.cprPricingService
        .updateCpr(
          priceDetail?.productNumber,
          +roundedPrice,
          getRequestType(this.priceRadioSelected, priceDetail),
          this.districtNgpCprInd,
          this.priceUom,
          false,
          this.tandemSystem,
        )
        .pipe(take(1))
        .subscribe((resp: CprPricingState) => {
          this.handleCprResponse(resp, roundedPrice);
        });
    });

    this.closeModal();
  }

  async handleCprResponse(resp: CprPricingState, priceToUpdate: number) {
    this.traceCpr(priceToUpdate, resp);

    switch (resp.unifiedResponseCode * 1) {
      //success response
      case 200:
        this.presentSuccessToastMessage(
          'i18n.orderPage.cprNgpModal.cprUpdateSuccessMessage',
        );
        this.loadingSpinnerService.dismissSpinnerModal();

        break;

      //manual approval
      case 300:
        await this.customPricingRationaleManualModalService.openModal(
          priceToUpdate,
          true,
        );
        this.handleCprManualApprovalRequest(
          this.customPricingRationaleManualModalService.isManual,
          this.customPricingRationaleManualModalService.reason,
          priceToUpdate,
        );
        this.loadingSpinnerService.dismissSpinnerModal();

        break;

      //below hard floor
      case 400:
        this.presentErrorToastMessage(
          'i18n.orderPage.cprNgpModal.hardFloorErrorMessage',
        );
        this.loadingSpinnerService.dismissSpinnerModal();

        break;

      //above hard ceiling
      case 401:
        this.presentErrorToastMessage(
          'i18n.orderPage.cprNgpModal.hardCeilingErrorMessage',
        );
        this.loadingSpinnerService.dismissSpinnerModal();

        break;

      default:
        this.presentErrorToastMessage(
          'i18n.orderPage.cprNgpModal.cprUpdateFailMessage',
        );
        this.loadingSpinnerService.dismissSpinnerModal();

        break;
    }
  }

  handleCprManualApprovalRequest(
    confirmManual: boolean | string,
    reason: string,
    priceToUpdate: number,
  ) {
    if (confirmManual) {
      const priceDetail = !this.isSubstitute
        ? this.sellerPriceDetail
        : this.altSellerPriceDetail;
      this.traceCpr(priceToUpdate, null, true);
      this.cprPricingServiceSub$ = this.cprPricingService
        .updateCpr(
          priceDetail?.productNumber,
          +priceToUpdate,
          getRequestType(this.priceRadioSelected, priceDetail),
          this.districtNgpCprInd,
          this.priceUom,
          true,
          this.tandemSystem,
          reason,
        )
        .pipe(take(1))
        .subscribe((resp: CprPricingState) => {
          this.handleCprManualApprovalResponse(resp, priceToUpdate);
        });
    } else {
      this.presentErrorToastMessage(
        'i18n.orderPage.cprNgpModal.manualCprApprovalNotSubmitted',
      );
    }
  }

  handleCprManualApprovalResponse(
    resp: CprPricingState,
    priceToUpdate: number,
  ) {
    this.traceCpr(priceToUpdate, resp, true);
    resp.unifiedResponseCode === 201
      ? this.presentSuccessToastMessage(
          'i18n.orderPage.cprNgpModal.manualCprApprovalSubmitted',
        )
      : //second cpr request is fire and forget, but error should be logged
        console.log('error received in manual CPR update response: ', resp);
  }

  unitPriceOverride(priceToUpdate: number): void {
    const ascCharge = this.prices?.additionalPricingInfo?.unitAscRate || 0;
    let newPrice;
    if (
      this.sellerPriceDetail?.customerAscInd === ShippingChargeEnum.included
    ) {
      newPrice = priceToUpdate - ascCharge < 0 ? ascCharge : priceToUpdate;
    } else {
      newPrice = priceToUpdate < 0 ? 0 : priceToUpdate;
    }
    let unitPriceOverride =
      this.sellerPriceDetail?.customerAscInd === ShippingChargeEnum.included
        ? priceToUpdate - ascCharge
        : priceToUpdate;
    const priceUpdateData = {
      productNumber: this.prices?.productNumber,
      unitPrice: roundPrice(newPrice),
      unitPriceOverride: roundPrice(unitPriceOverride),
    };
    this.modalController.dismiss(priceUpdateData);
  }

  presentErrorToastMessage(translateMessage: string): void {
    this.toastService.presentToastMsg(
      this.translateService.instant(translateMessage),
      'red-toast',
      MessageTypeEnum.failure,
      [],
    );
  }

  presentSuccessToastMessage(translateMessage: string): void {
    this.toastService.presentToastMsg(
      this.translateService.instant(translateMessage),
      'green-toast',
      MessageTypeEnum.success,
      [],
    );
  }

  toggleTab(tab: number) {
    if (tab === 2) {
      this.prices = this.altHandPricingDetail;
      this.priceRadios = getPricesForPriceRadios(this.altSellerPriceDetail);
    } else {
      this.prices = this.sellerHandPricingDetail;
      this.priceRadios = getPricesForPriceRadios(this.sellerPriceDetail);
    }
    if (this.currentTab !== tab) {
      this.currentTab = tab !== 2 ? 1 : 2;
      this.isSubstitute = !this.isSubstitute;
    }
  }

  togglePriceCPR(tab: number) {
    this.tabSwitched = true;
    const setPrice = document.querySelector('div.set-price');
    const setCPR = document.querySelector('div.set-cpr');

    if (this.platformService.isTouch.value) {
      if (tab === 2) {
        setPrice.classList.add('hide');
        setCPR.classList.remove('hide');
      } else {
        setCPR.classList.add('hide');
        setPrice.classList.remove('hide');
      }

      if (this.currentTab !== tab) {
        this.currentTab = tab !== 2 ? 1 : 2;
      }
    }
  }
  toggleAdditionalPricingLabel(ev: any) {
    ev.detail.value
      ? (this.additionalPricingAccordionLabel =
          'i18n.orderPage.cprNgpModal.closeAdditionalPricing')
      : (this.additionalPricingAccordionLabel =
          'i18n.orderPage.cprNgpModal.additionalPricing');
  }

  setPriceChange() {
    const { currentPrice, lastPrice } = this.sellerPriceDetail;
    this.priceChange = currentPrice.unitPrice - lastPrice.unitPrice;
  }

  radioPriceChange() {
    const maximumPrice = this.priceRadios?.get(RadioOptions.maximum).price;
    const minimumPrice = this.priceRadios?.get(RadioOptions.minimum).price;
    const targetPrice = this.priceRadios?.get(RadioOptions.target).price;

    if (this.priceRadioSelected === RadioOptions.target) {
      this.isInputDisabled = true;
      this.priceInputSelected = '';
      this.targetDifferencePercentage = 0;
      this.targetDifference = 0;
    } else if (this.priceRadioSelected === RadioOptions.maximum) {
      this.isInputDisabled = true;
      this.priceInputSelected = '';
      this.targetDifference = maximumPrice - targetPrice;
      this.targetDifferencePercentage = (maximumPrice / targetPrice - 1) * 100;
    } else if (this.priceRadioSelected === RadioOptions.minimum) {
      this.isInputDisabled = true;
      this.priceInputSelected = '';
      this.targetDifference = minimumPrice - targetPrice;
      this.targetDifferencePercentage = (minimumPrice / targetPrice - 1) * 100;
    } else {
      this.isInputDisabled = false;
      this.setCustomPriceTargetDifference();
    }
  }

  setCustomPriceTargetDifference() {
    if (this.priceInputSelected) {
      const customPrice = parseFloat(this.priceInputSelected);
      const targetPrice = this.priceRadios?.get(RadioOptions?.target).price;
      this.targetDifference = customPrice - targetPrice;
      this.targetDifferencePercentage = (customPrice / targetPrice - 1) * 100;
    }
  }

  selectRadio(type) {
    this.priceRadioSelected = type;
    this.userHasSelectedPrice = true;
  }

  selectMakeCpr() {
    this.isMakeCprSelected = !this.isMakeCprSelected;
  }

  closeModal() {
    this.modalController.dismiss();
  }

  traceCpr(
    priceToUpdate: number,
    response?: CprPricingState,
    isManualApproval?: boolean,
  ) {
    const prodPrice = !this.isSubstitute
      ? this.sellerPriceDetail?.currentPrice?.unitPrice
      : this.altSellerPriceDetail?.currentPrice?.unitPrice;

    const prodTracingData = this.productTracing.filter(
      product =>
        product.apn ==
        (!this.isSubstitute
          ? this.sellerPriceDetail?.productNumber
          : this.altSellerPriceDetail?.productNumber),
    );
    prodTracingData.map(prod => {
      if (!prod.productPrice && !prod.productOverridePrice) {
        prod.productPrice = prodPrice;
        prod.productOverridePrice = priceToUpdate;
      }
    });

    const errorDetails =
      response?.loadingState === 'error' ||
      response?.unifiedResponseCode * 1 === 300
        ? {
            responseCode: response?.unifiedResponseCode,
            returnText: response?.customerList[0]?.productList[0]?.returnText,
          }
        : '';
    if (!response) {
      this.orderTracingService.traceOrderCPROverrideCall(
        this.orderHeader,
        [...prodTracingData],
        !!isManualApproval,
      );
    } else if (
      (!!response && !response.unifiedResponseCode) ||
      response.unifiedResponseCode === 500
    ) {
      this.orderTracingService.traceOrderCPROverrideCallFailure(
        this.orderHeader,
        errorDetails,
        [...prodTracingData],
        !!isManualApproval,
      );
    } else {
      this.orderTracingService.traceOrderCPROverrideCallSuccess(
        this.orderHeader,
        errorDetails,
        [...prodTracingData],
        !!isManualApproval,
      );
    }
  }

  allowOnlyNumbers(event) {
    return allowOnlyNumbers(event);
  }

  get targetDisplayPrice() {
    return this.sellerPriceDetail?.customerAscInd !==
      ShippingChargeEnum.included
      ? this.priceRadios?.get(RadioOptions.target).price
      : this.priceRadios?.get(RadioOptions.target).price +
          this.prices?.additionalPricingInfo?.unitAscRate;
  }
  get optimalDisplayPrice() {
    return this.sellerPriceDetail?.customerAscInd !==
      ShippingChargeEnum.included
      ? this.priceRadios?.get(RadioOptions.maximum).price
      : this.priceRadios?.get(RadioOptions.maximum).price +
          this.prices?.additionalPricingInfo?.unitAscRate;
  }

  loadDivision() {
    this.currentDivisionSub$ = this.customerStoreService
      .loadSelectedCustomerDivision$()
      .subscribe(division => {
        this.tandemSystem = division?.tandemSystem;
      });
  }
}
